export const ADMISSION_REFUSAL_REASON = [
    "platform_at_capacity",
    "workspace_at_tier_ceiling",
    "agent_at_ceiling",
    "credit_exhausted",
    "trial_exhausted",
    "hard_stop_reached",
    "budget_reached",
    "pool_draining",
    "concurrency_entitlement_reached",
    "account_suspended_nonpayment",
    "entitlement_exhausted",
    "workspace_draining",
    "account_closing",
    "talk_rate_unclassified",
    "talk_rate_missing",
];
/**
 * The Redis keys of design 7.2, named here because the sweeper and the capacity poller both
 * read them and a key-name drift between units is a silent capacity bug.
 *
 * `{conv}:live` is a ZSET scored by last heartbeat, not a counter, precisely so a dead
 * replica's conversations age out instead of leaking forever.
 *
 * **Every conversation key carries the hash tag `{conv}`.** Production Redis (Azure Managed Redis)
 * is clustered, and a cluster runs a Lua script only when every key it touches hashes to one slot.
 * The admission scripts touch the live set, the prefix hash, the sweeper's index and the
 * per-workspace sets together; without the shared tag each lands on its own slot and every
 * admission and every sweep is refused with `CROSSSLOT` (seen in production 2026-09-24).
 */
export const CONVERSATION_KEY_HASH_TAG = "{conv}";
export const ADMISSION_REDIS_KEY = {
    /** ZSET member=`conversation_id`, score=last heartbeat epoch seconds. */
    liveConversation: `${CONVERSATION_KEY_HASH_TAG}:live`,
    /**
     * ZSET member=`conversation_id`, score=its LAST heartbeat epoch seconds, written by the sweep
     * as it removes a stale member from `{conv}:live` (ADDED 2026-09-26, review of plan-004, open
     * item 1). It is the evidence of life a dead replica leaves behind: the worker's conversation
     * sweep (`apps/worker/src/handler/conversation-sweep`) ends the conversation's row at that
     * second — never at "now" — and then removes the member. A normal release never writes here.
     */
    lostConversation: `${CONVERSATION_KEY_HASH_TAG}:lost`,
    /** ZSET, per workspace tier ceiling. */
    liveConversationOfWorkspace: (workspaceId) => `${CONVERSATION_KEY_HASH_TAG}:live:ws:${workspaceId}`,
    /** HASH field=`{agent_version_id}:{language}` -> count. This is K. */
    livePrefix: `${CONVERSATION_KEY_HASH_TAG}:prefix`,
    /** The HASH field spelling. K counts (agent_version, language) pairs, never VoiceAgents. */
    livePrefixField: (agentVersionId, language) => `${agentVersionId}:${language}`,
    /** STRING, the drain override, set while a pool is Draining. */
    gpuPoolCeilingOverride: (gpuPoolId) => `pool:${gpuPoolId}:ceiling`,
    /** STRING, ready replica count, written by the capacity poller — never desired state. */
    gpuPoolReadyReplica: (gpuPoolId) => `pool:${gpuPoolId}:ready`,
    /**
     * ZSET member=`conversation_id`, score=last heartbeat epoch seconds: the conversations PLACED on one
     * `gpu_pool` (plan-007, design §19.5.3, IC-63; ADDED 2026-09-28). Carries the `{conv}` hash tag, so it
     * shares the slot of every key above and the admission scripts may touch it with them.
     */
    liveConversationOfGpuPool: (gpuPoolId) => `${CONVERSATION_KEY_HASH_TAG}:live:gpu_pool:${gpuPoolId}`,
    /**
     * The fixed part of {@link ADMISSION_REDIS_KEY.liveConversationOfGpuPool}, which the scripts append a
     * pool id to: a sweep removes members from sets it discovers through the meta index and so cannot
     * declare as KEYS (ADDED 2026-09-28).
     */
    liveConversationOfGpuPoolPrefix: `${CONVERSATION_KEY_HASH_TAG}:live:gpu_pool:`,
    /**
     * ZSET member=`conversation_id`, score=last heartbeat epoch seconds: the conversations placed on the
     * MODEL BACKUP (plan-010, design §24.4, IC-83; ADDED 2026-10-01). Written by the admit and move scripts,
     * the heartbeat, the sweep and the release, exactly as a pool's set is. Carries the `{conv}` hash tag.
     */
    liveConversationOfModelBackup: `${CONVERSATION_KEY_HASH_TAG}:live:model_backup`,
    /**
     * STRING `on` | `off`: the operator switch of the model backup (plan-010, design §24.4, §24.7, DD10-6;
     * ADDED 2026-10-01). Written by the operator API only (step-up, audit-or-abort); **an absent key reads
     * `off`**. No hash tag: no admission script touches it — the runtime's backup standing reads it.
     */
    modelBackupSwitch: "model_backup:switch",
    /**
     * ZSET, the same shape as {@link ADMISSION_REDIS_KEY.liveConversationOfModelBackup}: the conversations placed on the
     * SECOND model backup (plan-027 DM-SMB, design §38.5, ADR 0079; ADDED 2026-10-23). The first backup's set keeps its
     * spelling. Carries the `{conv}` hash tag.
     */
    liveConversationOfSecondModelBackup: `${CONVERSATION_KEY_HASH_TAG}:live:model_backup:second`,
    /**
     * STRING `on` | `off`: the SECOND model backup's own operator switch (plan-027, design §38.7, DD27-7; ADDED
     * 2026-10-23). Written by the operator API only; **an absent key reads `off`**. No hash tag: no script touches it.
     */
    secondModelBackupSwitch: "model_backup:second:switch",
    /**
     * ZSET member=`platform_replica_name` (the runtime process's own host name), score=its last presence
     * beat epoch seconds: the agent_runtime processes alive right now (the admission cap, design §7.2 AS
     * BUILT 2026-10-04; ADDED 2026-10-04). Each runtime process beats every `ADMISSION_HEARTBEAT.intervalMs`;
     * a member older than `ADMISSION_HEARTBEAT.staleAfterMs` is not counted and the next beat removes it, so
     * a dead process stops counting on the same 30 s window its conversations age out on. Admission reads the
     * count for its runtime bound (`present × runtime.conversation_per_platform_replica`); the operator
     * Capacity screen reads the members. Carries the `{conv}` hash tag beside the live set.
     */
    platformReplicaPresence: `${CONVERSATION_KEY_HASH_TAG}:platform_replica`,
    /**
     * HASH field=`platform_replica_name` -> the conversations that process carried at its last beat (ADDED
     * 2026-10-04): the per-process figure the Capacity screen shows beside the runtime bound. Written and
     * pruned with {@link ADMISSION_REDIS_KEY.platformReplicaPresence}, never read by admission.
     */
    platformReplicaInUse: `${CONVERSATION_KEY_HASH_TAG}:platform_replica:in_use`,
    /**
     * ZSET member=`conversation_id`, score=last heartbeat epoch seconds: the live conversations of ONE organization —
     * the counter of its commercial concurrency entitlement (plan-014 E3a, design §27.12.1, DD14-6; ADDED 2026-10-06).
     * Written by the admit script beside `{conv}:live`, refreshed by the heartbeat, pruned by release and sweep, and
     * compared (fresh members only) with `concurrencyEntitlementOf(account)`. Carries the `{conv}` hash tag.
     */
    liveConversationOfAccount: (accountId) => `${CONVERSATION_KEY_HASH_TAG}:live:acct:${accountId}`,
    /** The fixed part of {@link ADMISSION_REDIS_KEY.liveConversationOfAccount}, which the scripts append an account id to. */
    liveConversationOfAccountPrefix: `${CONVERSATION_KEY_HASH_TAG}:live:acct:`,
    /**
     * HASH field=`conversation_id` -> `account_id`: which organization's set a live member is in (ADDED 2026-10-06), so
     * release, heartbeat and sweep find it without changing the three-segment meta value an earlier build reads.
     */
    liveConversationAccountIndex: `${CONVERSATION_KEY_HASH_TAG}:live:account`,
    /**
     * ZSET member=`conversation_id` (never a turn id), score=last heartbeat epoch seconds: the HELP TURNS in flight, on
     * any route (plan-019 ADM-DB, design §31.3.7, §31.6.5, ADR 0066; ADDED 2026-10-15). Written only by the help scripts,
     * admitted and released around each model turn of a help conversation, trimmed by the help admit script itself — a
     * stale help turn is simply gone and NEVER reaches `{conv}:lost`. No customer script, sweep or reader touches it, and
     * it holds nothing a customer rung counts. Carries the `{conv}` hash tag so the help script may read the customers'
     * placed sets beside it.
     */
    liveHelpTurn: `${CONVERSATION_KEY_HASH_TAG}:live:help`,
    /**
     * ZSET, the same shape as {@link ADMISSION_REDIS_KEY.liveHelpTurn}: the subset of the help turns in flight that the
     * help admit script placed on the MODEL BACKUP (the help spill, OD-18; ADDED 2026-10-15). Never the customers'
     * `{conv}:live:model_backup`.
     */
    liveHelpTurnOfModelBackup: `${CONVERSATION_KEY_HASH_TAG}:live:help:model_backup`,
    /**
     * ZSET: the help turns in flight placed on the SECOND model backup (plan-027, design §38.5, DD27-14; ADDED
     * 2026-10-23). Never the customers' `{conv}:live:model_backup:second`.
     */
    liveHelpTurnOfSecondModelBackup: `${CONVERSATION_KEY_HASH_TAG}:live:help:model_backup:second`,
    /**
     * ZSET member=`conversation_id`, score=the help socket's last beat epoch seconds: the OPEN help conversations (an
     * attached help socket), counted against the open-conversation limit (OD-11's 20; ADDED 2026-10-15). A
     * socket-lifetime count, apart from the turn sets; read by no customer rung.
     */
    openHelpConversation: `${CONVERSATION_KEY_HASH_TAG}:help:open`,
    /**
     * HASH field=`conversation_id`, value=`{subject}|{prefix}`: who holds each open help conversation — `p:{person_id}`
     * or `v:{sha256(visitor key)}`, and for an anonymous visitor `n:{sha256(network prefix)}` (empty when signed in). The
     * open script counts the per-person, per-visitor, per-prefix and anonymous shares from it (plan-019 review fix,
     * 2026-10-15). Written and trimmed with {@link ADMISSION_REDIS_KEY.openHelpConversation}; read by no customer rung.
     */
    openHelpConversationOwner: `${CONVERSATION_KEY_HASH_TAG}:help:open:owner`,
};
/** The heartbeat cadence and sweep window of design 7.2, in milliseconds. */
export const ADMISSION_HEARTBEAT = {
    /** The supervisor heartbeats its conversation every 10 s. */
    intervalMs: 10_000,
    /** The sweeper removes members whose score is older than 30 s. */
    staleAfterMs: 30_000,
    /**
     * How long a `{conv}:lost` member is kept when nobody collects it (ADDED 2026-09-26): the sweep
     * trims members whose last heartbeat is older than this, so a worker that is not running cannot
     * make the set grow without bound. Seven days, the outbox's own retry window.
     */
    lostRetentionMs: 7 * 86_400_000,
};
//# sourceMappingURL=admission.js.map